import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { throttle } from 'lodash';
import { useConfig } from '../../hooks/useConfig';
import { KEEP_ALIVE } from '../../graphql/GqlProvider';
/**
 * Component that monitors user activity and after inactivity period shows a logout warning modal (LogoutModal)
 *
 * @component
 * @param {LoginActivityMonitorProps} props - Component props
 * @returns {JSX.Element} Rendered component
 *
 * @description
 * Monitors user activity (mouse, keyboard, touch, scroll) and:
 * - Shows a warning modal after period of inactivity (default 50 minutes)
 * - Forces logout after additional period (default 5 minutes)
 * - Sends keepalive requests to server periodically while active
 * - Resets timers on any user activity
 */
export const LoginActivityMonitor = ({ LogoutModal, children }) => {
    const config = useConfig();
    const [showModal, setShowModal] = useState(false);
    const [logoutActionExpireSec, setLogoutActionExpireSec] = useState(5 * 60); // default to 5 minutes
    const [keepAlive] = useMutation(KEEP_ALIVE);
    /**
     * Handles closing the logout warning modal
     */
    function handleClose() {
        setShowModal(false);
    }
    useEffect(() => {
        if (showModal)
            return;
        // The backend ACCESS_TOKEN_EXPIRE_SECONDS is 3600 seconds (60 minutes)
        // ACCESS_TOKEN_EXPIRE_SECONDS > LOGOUT_SHOW_MODAL_EXPIRE_SEC + LOGOUT_ACTION_EXPIRE_SEC
        if (config?.logoutActionExpireSec)
            setLogoutActionExpireSec(config?.logoutActionExpireSec);
        const logoutShowModalExpireSec = config?.logoutShowModalExpireSec || 50 * 60; // default to 50 minutes
        const logoutKeepAliveIntervalSec = config?.logoutKeepAliveIntervalSec || 30; // default to 30 seconds
        const throttleKeepAlive = throttle(keepAlive, logoutKeepAliveIntervalSec * 1000); // max every X seconds
        let showModalTimerId = null;
        /**
         * Handles any user activity by resetting timers and sending keepalive
         */
        function onActivity() {
            cleanHooks(showModalTimerId);
            setTimeout(startShowModalTimer, 100);
            throttleKeepAlive();
        }
        /**
         * Cleans up event listeners and timers
         * @param {any} timerId - Timer ID to clear
         */
        function cleanHooks(timerId) {
            if (timerId) {
                clearTimeout(timerId);
                timerId = null;
            }
            document.removeEventListener('keypress', onActivity);
            document.removeEventListener('mousemove', onActivity);
            document.removeEventListener('touchmove', onActivity);
            document.removeEventListener('wheel', onActivity);
            document.removeEventListener('scroll', onActivity);
        }
        /**
         * Starts the timer for showing the logout warning modal and sets up activity listeners
         */
        function startShowModalTimer() {
            showModalTimerId = setTimeout(onShowModalTimerExpires, logoutShowModalExpireSec * 1000);
            document.addEventListener('keypress', onActivity);
            document.addEventListener('mousemove', onActivity);
            document.addEventListener('touchmove', onActivity);
            document.addEventListener('wheel', onActivity);
            document.addEventListener('scroll', onActivity);
        }
        /**
         * Handles timer expiration by showing the logout warning modal
         */
        function onShowModalTimerExpires() {
            cleanHooks(showModalTimerId);
            setShowModal(true);
        }
        startShowModalTimer();
        return () => {
            cleanHooks(showModalTimerId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal, config]);
    return (_jsxs(_Fragment, { children: [showModal && _jsx(LogoutModal, { onClose: handleClose, logoutExpireSec: logoutActionExpireSec }), children] }));
};
